/**
 * ExportToolbar — Drop-in export menu for any data table.
 * Supports PDF, CSV, Excel downloads and email sending.
 *
 * Usage:
 *   <ExportToolbar
 *     title="My Report"
 *     rows={data}
 *     columns={[{field: 'name', headerName: 'Name'}, ...]}
 *     periodLabel="March 2026"
 *     filenamePrefix="my-report"
 *   />
 */
import { useState } from "react";
import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./dialog";
import { Input } from "./input";
import { Label } from "./label";
import { Textarea } from "./textarea";
import { toast } from "sonner";
import { Download, FileText, FileSpreadsheet, Mail, Loader2, ChevronDown } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ExportToolbar({
  title = "Report",
  rows = [],
  columns = [],
  periodLabel = "",
  filenamePrefix = "report",
  disabled = false,
  reportType = "generic",
  disableEmail = false,
  brandConfig,
  groupField,
  groupTotalRows,
  grandTotalRow,
}) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sending, setSending] = useState(false);

  const isEmpty = !rows || rows.length === 0;

  // ── PDF Export ─────────────────────────────────────────────────────────
  const exportPdf = () => {
    const doc = new jsPDF({
      orientation: columns.length > 5 ? "landscape" : "portrait",
      unit: "pt",
    });

    const primaryColor = brandConfig?.primaryColor || "#1e40af";

    // Title
    doc.setFontSize(16);
    doc.setTextColor(primaryColor);
    doc.text(title, 40, 40);
    if (periodLabel) {
      doc.setFontSize(10);
      doc.setTextColor("#64748b");
      doc.text(periodLabel, 40, 56);
    }

    // Table
    const headers = columns.map((c) => c.headerName || c.field);
    const body = rows.map((row) =>
      columns.map((c) => {
        const val = row[c.field];
        if (c.format) return c.format(val);
        return val != null ? String(val) : "";
      })
    );

    autoTable(doc, {
      head: [headers],
      body,
      startY: periodLabel ? 68 : 52,
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: {
        fillColor: primaryColor,
        textColor: "#ffffff",
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: "#f8fafc" },
      margin: { left: 40, right: 40 },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor("#94a3b8");
      doc.text(
        `Generated ${new Date().toLocaleString()} | Page ${i} of ${pageCount}`,
        40,
        doc.internal.pageSize.height - 20
      );
    }

    doc.save(`${filenamePrefix}.pdf`);
    toast.success("PDF downloaded");
  };

  // ── CSV Export ─────────────────────────────────────────────────────────
  const exportCsv = () => {
    const headers = columns.map((c) => c.headerName || c.field);
    const csvRows = [headers.join(",")];
    for (const row of rows) {
      const vals = columns.map((c) => {
        let val = row[c.field];
        if (c.format) val = c.format(val);
        val = val != null ? String(val) : "";
        // Escape commas and quotes
        if (val.includes(",") || val.includes('"') || val.includes("\n")) {
          val = `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      });
      csvRows.push(vals.join(","));
    }
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filenamePrefix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  // ── Excel Export ───────────────────────────────────────────────────────
  const exportExcel = () => {
    const headers = columns.map((c) => c.headerName || c.field);
    const data = rows.map((row) =>
      columns.map((c) => {
        const val = row[c.field];
        if (c.format) return c.format(val);
        return val != null ? val : "";
      })
    );
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${filenamePrefix}.xlsx`);
    toast.success("Excel downloaded");
  };

  // ── Build HTML table for email ─────────────────────────────────────────
  const buildHtmlTable = () => {
    const headers = columns.map((c) => c.headerName || c.field);
    let html = `<h2 style="color:#1e40af;margin-bottom:4px">${title}</h2>`;
    if (periodLabel) html += `<p style="color:#64748b;font-size:12px;margin-bottom:12px">${periodLabel}</p>`;
    html += `<table style="border-collapse:collapse;width:100%;font-size:12px;font-family:Arial,sans-serif">`;
    html += `<tr>${headers.map((h) => `<th style="background:#1e40af;color:white;padding:6px 8px;text-align:left">${h}</th>`).join("")}</tr>`;
    rows.forEach((row, i) => {
      const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
      html += `<tr style="background:${bg}">${columns.map((c) => {
        let val = row[c.field];
        if (c.format) val = c.format(val);
        return `<td style="padding:4px 8px;border-bottom:1px solid #e2e8f0">${val != null ? val : ""}</td>`;
      }).join("")}</tr>`;
    });
    html += `</table>`;
    return html;
  };

  // ── Email Send ─────────────────────────────────────────────────────────
  const sendEmail = async () => {
    if (!emailTo.trim()) {
      toast.error("Please enter a recipient email");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/report/email`, {
        to: emailTo.split(",").map((e) => e.trim()),
        cc: emailCc ? emailCc.split(",").map((e) => e.trim()) : [],
        subject: `${title} — ${periodLabel}`,
        message: emailMessage,
        html: buildHtmlTable(),
        reportType,
        periodLabel,
      });
      toast.success("Email sent successfully");
      setEmailOpen(false);
      setEmailTo("");
      setEmailCc("");
      setEmailMessage("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" disabled={disabled || isEmpty} className="gap-1.5">
            <Download className="h-4 w-4" />
            Export
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={exportPdf} className="gap-2 cursor-pointer">
            <FileText className="h-4 w-4 text-red-500" />
            Download PDF
          </DropdownMenuItem>
          <DropdownMenuItem onClick={exportCsv} className="gap-2 cursor-pointer">
            <FileText className="h-4 w-4 text-green-600" />
            Download CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={exportExcel} className="gap-2 cursor-pointer">
            <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
            Download Excel
          </DropdownMenuItem>
          {!disableEmail && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setEmailOpen(true)} className="gap-2 cursor-pointer">
                <Mail className="h-4 w-4 text-blue-500" />
                Send via Email
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Email Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-500" />
              Email Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>To (comma-separated)</Label>
              <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="user@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label>CC (optional)</Label>
              <Input value={emailCc} onChange={(e) => setEmailCc(e.target.value)} placeholder="manager@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Message (optional)</Label>
              <Textarea value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} placeholder="Please see the attached report..." rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={sending} className="gap-1.5">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
